/**
 * La mémoire de l'affaire — ce que les missions ont acquis, et que le
 * métreur administre.
 *
 * Deux registres. Les **décisions** : chaque arbitrage rendu au guichet
 * s'applique à toute mission suivante sans être redemandé — en retirer un
 * le rend redemandable. Les **relevés** : les fichiers dérivés des plans
 * que les agents relisent au lieu de refaire le travail — en retirer un le
 * fait refaire. L'écran montre et retire, rien d'autre : la mémoire
 * s'écrit par les missions — à une exception près, assumée : **poser une
 * décision d'avance**. Le métreur qui sait déjà que la hauteur vaut 2,50 m
 * partout l'inscrit ici, et la mission qui rencontrera ce point le trouvera
 * fermé. Modifier ne change que la réponse : le point reste le même, c'est
 * lui que le guichet et les briefs reconnaissent.
 */
import { useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { Avis } from "../../composants/avis";
import { useModale } from "../../composants/clavier";
import { Chargement, Echec, Vide } from "../../composants/etats";
import { poids, quand } from "../../composants/formats";
import { useDonnees } from "../../composants/donnees";
import { Atelier } from "./Atelier";

interface Decision {
  empreinte: string;
  local: string;
  question: string;
  reponse: string;
  mission_id: string;
  quand: string;
}

interface Releve {
  chemin: string;
  nom: string;
  octets: number;
  modifie_le: string;
}

type Suppression =
  | { genre: "decision"; decision: Decision }
  | { genre: "releve"; releve: Releve };

export function Memoire({ affaireId }: { affaireId: string }) {
  const [suppression, poserSuppression] = useState<Suppression | null>(null);
  const [atelier, poserAtelier] = useState<{
    releve: Releve; mode: "progressif" | "direct";
  } | null>(null);
  const [edition, poserEdition] = useState<Decision | null>(null);
  const [reponseEditee, poserReponseEditee] = useState("");
  const [ajout, poserAjout] = useState(false);
  const [brouillon, poserBrouillon] = useState(
    { local: "", question: "", reponse: "" });
  const [filtre, poserFiltre] = useState("");
  const [envoi, poserEnvoi] = useState(false);

  // Échap ferme la modale ouverte — jamais pendant un envoi, où l'on ne quitte
  // pas d'un coup de touche.
  const refAjout = useModale(ajout && !envoi, () => poserAjout(false));
  const refEdition = useModale(edition !== null && !envoi, () => poserEdition(null));
  const refSuppression = useModale(
    suppression !== null && !envoi, () => poserSuppression(null));
  const [erreur, poserErreur] = useState("");
  const [erreurModale, poserErreurModale] = useState("");

  const memoire = useDonnees<{
    donnees: { decisions: Decision[]; releves: Releve[] };
  }>(() => api.lire(`/api/v1/affaires/${affaireId}/memoire`), [affaireId]);

  if (memoire.charge && !memoire.donnees) return <Chargement lignes={3} />;
  if (memoire.erreur) {
    return <Echec erreur={memoire.erreur} reessayer={memoire.recharger} />;
  }

  const decisions = memoire.donnees?.donnees.decisions ?? [];
  const releves = memoire.donnees?.donnees.releves ?? [];
  const aiguille = filtre.trim().toLowerCase();
  const visibles = aiguille
    ? decisions.filter((d) =>
        [d.local, d.question, d.reponse].join(" ").toLowerCase()
          .includes(aiguille))
    : decisions;

  async function poser() {
    poserErreurModale("");
    poserEnvoi(true);
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/memoire/arbitrages`, brouillon);
      poserAjout(false);
      poserBrouillon({ local: "", question: "", reponse: "" });
      memoire.recharger();
    } catch (exc) {
      poserErreurModale(exc instanceof ErreurApi ? exc.message
                        : "La décision n'a pas été posée.");
    } finally {
      poserEnvoi(false);
    }
  }

  async function modifier() {
    if (!edition) return;
    poserErreurModale("");
    poserEnvoi(true);
    try {
      await api.modifier(
        `/api/v1/affaires/${affaireId}/memoire/arbitrages`
        + `/${edition.empreinte}`, { reponse: reponseEditee });
      poserEdition(null);
      memoire.recharger();
    } catch (exc) {
      poserErreurModale(exc instanceof ErreurApi ? exc.message
                        : "La modification n'a pas abouti.");
    } finally {
      poserEnvoi(false);
    }
  }

  async function retirer(cible: Suppression) {
    poserErreur("");
    poserEnvoi(true);
    try {
      if (cible.genre === "decision") {
        await api.retirer(
          `/api/v1/affaires/${affaireId}/memoire/arbitrages`
          + `/${cible.decision.empreinte}`);
      } else {
        await api.retirer(
          `/api/v1/affaires/${affaireId}/memoire/releves`
          + `?chemin=${encodeURIComponent(cible.releve.chemin)}`);
      }
      poserSuppression(null);
      memoire.recharger();
    } catch (exc) {
      poserSuppression(null);
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "Le retrait n'a pas abouti.");
    } finally {
      poserEnvoi(false);
    }
  }

  return (
    <div className="pile">
      {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}

      <div className="carte">
        <div className="carte__tete">
          <h3>Décisions du métreur</h3>
          {decisions.length > 3 && (
            <input
              className="memoire__filtre"
              value={filtre}
              placeholder="Filtrer…"
              aria-label="Filtrer les décisions"
              onChange={(e) => poserFiltre(e.target.value)}
            />
          )}
          <button type="button" className="bouton"
                  onClick={() => {
                    poserErreurModale("");
                    poserAjout(true);
                  }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Poser une décision
          </button>
        </div>
        {decisions.length === 0 ? (
          <Vide
            titre="Aucune décision retenue"
            aide="Les arbitrages rendus au guichet de l'assistant s'inscrivent ici, et les missions suivantes les appliquent."
          />
        ) : (
          <div className="defilant">
            <table className="tableau">
              <thead>
                <tr>
                  <th>Local ou portée</th>
                  <th>Point tranché</th>
                  <th>Décision</th>
                  <th>Origine</th>
                  <th>Quand</th>
                  <th aria-label="Gestes" />
                </tr>
              </thead>
              <tbody>
                {visibles.map((decision) => (
                  <tr key={decision.empreinte}>
                    <td>{decision.local || "—"}</td>
                    <td className="memoire__question">{decision.question}</td>
                    <td className="memoire__reponse">
                      <strong>{decision.reponse}</strong>
                    </td>
                    <td>
                      <span className="etiquette" title={decision.mission_id
                        ? `mission ${decision.mission_id}` : "origine inconnue"}>
                        {decision.mission_id === "portail" ? "métreur"
                          : decision.mission_id
                            ? `mission ${decision.mission_id.slice(-6)}`
                            : "—"}
                      </span>
                    </td>
                    <td className="discret">{quand(decision.quand)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="action"
                        title="Modifier la décision — le point reste le même"
                        aria-label={`Modifier la décision sur ${decision.local || "ce point"}`}
                        onClick={() => {
                          poserErreurModale("");
                          poserReponseEditee(decision.reponse);
                          poserEdition(decision);
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             aria-hidden="true">
                          <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="action"
                        title="Retirer — le point pourra être redemandé"
                        aria-label={`Retirer la décision sur ${decision.local || "ce point"}`}
                        onClick={() => poserSuppression(
                          { genre: "decision", decision })}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             aria-hidden="true">
                          <path d="M3 6h18M8 6V4h8v2m-9 0 1 14h8l1-14" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={6} className="tableau__vide">
                      Aucune décision ne répond au filtre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="carte">
        <div className="carte__tete">
          <h3>Relevés acquis</h3>
          <span className="discret">
            Les missions les relisent au lieu de refaire le travail.
          </span>
        </div>
        {releves.length === 0 ? (
          <Vide
            titre="Aucun relevé dérivé"
            aide="Les relevés de locaux et autres dérivés des plans apparaîtront ici après une première mission."
          />
        ) : (
          <div className="defilant">
            <table className="tableau">
              <thead>
                <tr>
                  <th>Fichier</th>
                  <th>Emplacement</th>
                  <th>Taille</th>
                  <th>Établi le</th>
                  <th aria-label="Gestes" />
                </tr>
              </thead>
              <tbody>
                {releves.map((releve) => (
                  <tr key={releve.chemin}>
                    <td className="mono">{releve.nom}</td>
                    <td className="discret tronque memoire__chemin">
                      {releve.chemin}
                    </td>
                    <td>{poids(releve.octets)}</td>
                    <td className="discret">{quand(releve.modifie_le)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="action"
                        title="Voir le plan reconstruit"
                        aria-label={`Voir le plan de ${releve.nom}`}
                        onClick={() => poserAtelier(
                          { releve, mode: "direct" })}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             aria-hidden="true">
                          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="action"
                        title="Reconstruire pas à pas — l'atelier"
                        aria-label={`Reconstruire ${releve.nom} pas à pas`}
                        onClick={() => poserAtelier(
                          { releve, mode: "progressif" })}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             aria-hidden="true">
                          <path d="M5 4l14 8-14 8V4Z" />
                        </svg>
                      </button>
                      <a
                        className="action"
                        title="Télécharger le relevé"
                        aria-label={`Télécharger le relevé ${releve.nom}`}
                        href={`/api/v1/affaires/${affaireId}/memoire/releves/fichier?chemin=${encodeURIComponent(releve.chemin)}`}
                        download={releve.nom}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             aria-hidden="true">
                          <path d="M12 3v12m0 0 5-5m-5 5-5-5M4 21h16" />
                        </svg>
                      </a>
                      <button
                        type="button"
                        className="action"
                        title="Retirer — il sera refait à la prochaine mission"
                        aria-label={`Retirer le relevé ${releve.nom}`}
                        onClick={() => poserSuppression(
                          { genre: "releve", releve })}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             aria-hidden="true">
                          <path d="M3 6h18M8 6V4h8v2m-9 0 1 14h8l1-14" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {ajout && (
        <div ref={refAjout} className="voile" role="dialog" aria-modal="true"
             aria-label="Poser une décision">
          <div className="modale" style={{ width: "min(560px, 100%)" }}>
            <div className="modale__tete"><h2>Poser une décision d'avance</h2></div>
            <div className="modale__corps"
                 style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                <p className="discret">
                  La mission qui rencontrera ce point le trouvera tranché —
                  aucun guichet ne vous le demandera.
                </p>
                <div className="champ">
                  <label htmlFor="memoire-local">Local ou portée (facultatif)</label>
                  <input id="memoire-local" value={brouillon.local}
                         placeholder="PMR, Niveau R+1, Toutes zones…"
                         onChange={(e) => poserBrouillon(
                           { ...brouillon, local: e.target.value })} />
                </div>
                <div className="champ">
                  <label htmlFor="memoire-question">Point à trancher</label>
                  <textarea id="memoire-question" rows={3}
                            value={brouillon.question}
                            placeholder="Ex. : hauteur sous faux plafond retenue pour les locaux sans cote ?"
                            onChange={(e) => poserBrouillon(
                              { ...brouillon, question: e.target.value })} />
                </div>
                <div className="champ">
                  <label htmlFor="memoire-reponse">Décision</label>
                  <input id="memoire-reponse" value={brouillon.reponse}
                         placeholder="Ex. : 2,50 m partout"
                         onChange={(e) => poserBrouillon(
                           { ...brouillon, reponse: e.target.value })} />
                </div>
                {erreurModale && (
                  <div className="avis avis--erreur">{erreurModale}</div>
                )}
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserAjout(false)}>
                Annuler
              </button>
              <button type="button" className="bouton bouton--primaire"
                      disabled={envoi || brouillon.question.trim().length < 3
                                || !brouillon.reponse.trim()}
                      onClick={() => void poser()}>
                Poser la décision
              </button>
            </div>
          </div>
        </div>
      )}

      {edition && (
        <div ref={refEdition} className="voile" role="dialog" aria-modal="true"
             aria-label="Modifier la décision">
          <div className="modale" style={{ width: "min(560px, 100%)" }}>
            <div className="modale__tete"><h2>Modifier la décision</h2></div>
            <div className="modale__corps"
                 style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                <p>
                  {edition.local && <strong>{edition.local} — </strong>}
                  {edition.question}
                </p>
                <div className="champ">
                  <label htmlFor="memoire-edition">Nouvelle décision</label>
                  <input id="memoire-edition" value={reponseEditee}
                         onChange={(e) => poserReponseEditee(e.target.value)} />
                </div>
                <p className="discret">
                  Le point reste le même : les missions suivantes liront la
                  nouvelle réponse, sans rien redemander.
                </p>
                {erreurModale && (
                  <div className="avis avis--erreur">{erreurModale}</div>
                )}
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserEdition(null)}>
                Annuler
              </button>
              <button type="button" className="bouton bouton--primaire"
                      disabled={envoi || !reponseEditee.trim()
                                || reponseEditee === edition.reponse}
                      onClick={() => void modifier()}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {atelier && (
        <Atelier
          affaireId={affaireId}
          chemin={atelier.releve.chemin}
          nom={atelier.releve.nom}
          mode={atelier.mode}
          fermer={() => poserAtelier(null)}
        />
      )}

      {suppression && (
        <div ref={refSuppression} className="voile" role="dialog" aria-modal="true"
             aria-label="Retirer de la mémoire">
          <div className="modale" style={{ width: "min(520px, 100%)" }}>
            <div className="modale__tete">
              <h2>
                {suppression.genre === "decision"
                  ? "Retirer cette décision ?"
                  : "Retirer ce relevé ?"}
              </h2>
            </div>
            <div className="modale__corps"
                 style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                {suppression.genre === "decision" ? (
                  <>
                    <p>
                      {suppression.decision.local && (
                        <strong>{suppression.decision.local} — </strong>
                      )}
                      {suppression.decision.question}
                      <br />
                      <strong>→ {suppression.decision.reponse}</strong>
                    </p>
                    <div className="avis avis--attention">
                      Le point redeviendra ouvert : la prochaine mission qui le
                      rencontrera vous le redemandera au guichet.
                    </div>
                  </>
                ) : (
                  <>
                    <p><strong>{suppression.releve.nom}</strong></p>
                    <div className="avis avis--attention">
                      Le relevé sera refait par la prochaine mission qui en aura
                      besoin — quelques minutes d'outils, aucune donnée du
                      dossier n'est perdue.
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserSuppression(null)}>
                Annuler
              </button>
              <button type="button" className="bouton bouton--danger"
                      disabled={envoi}
                      onClick={() => void retirer(suppression)}>
                {suppression.genre === "decision"
                  ? "Retirer la décision" : "Retirer le relevé"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
